import React from 'react';
import { type ClosureReport } from '../types.ts';
import { DownloadIcon } from './icons.tsx';
import { triggerDownload } from '../utils/download.ts';

interface ClosureReportPanelProps {
  reports: ClosureReport[];
}

const ClosureReportPanel: React.FC<ClosureReportPanelProps> = ({ reports }) => {

  const handleExportTxt = (report: ClosureReport) => {
    let content = `CLOSURE REPORT: ${report.name}\n`;
    content += `Generated: ${new Date(report.createdAt).toLocaleString()}\n`;
    content += `-------------------------------------------------\n\n`;
    content += `TRAVERSE DATA:\n`;
    report.lines.forEach(line => {
      content += `  ${line.from} -> ${line.to}: ${line.bearing} / ${line.distance}\n`;
    });
    content += `\n`;
    content += `STATISTICS:\n`;
    content += `  Total Distance: ${report.totalDistance.toFixed(2)}'\n`;
    content += `  Area: ${report.area.toFixed(2)} sq. ft. (${(report.area / 43560).toFixed(4)} acres)\n`;
    content += `\n`;
    content += `MISCLOSURE:\n`;
    content += `  Delta Northing: ${report.misclosureNorthing.toFixed(4)}'\n`;
    content += `  Delta Easting:  ${report.misclosureEasting.toFixed(4)}'\n`;
    content += `  Distance:       ${report.misclosureDistance.toFixed(4)}'\n`;
    content += `  Bearing:        ${report.misclosureBearing}\n`;
    content += `  Precision:      ${report.precision}\n`;
    content += `\n-------------------------------------------------\n`;
    content += `Disclaimer: Your requested data is ready for professional review.`;
    
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const safeName = report.name.replace(/[^a-z0-9._-]/gi, '_').toLowerCase();
    triggerDownload(blob, `closure_report_${safeName}.txt`);
  };

  return (
    <div className="w-full h-full bg-gray-900 flex flex-col rounded-md overflow-hidden light-theme:bg-white">
      <header className="flex-shrink-0 p-4 bg-gray-800/40 backdrop-blur border-b border-gray-700/30 light-theme:bg-gray-50/40 light-theme:border-gray-300/30">
        <h3 className="text-xl font-semibold text-gray-300 light-theme:text-gray-700">Closure Reports</h3>
        <p className="text-sm text-gray-400">View traverse closure reports generated by the AI.</p>
      </header>
      <div className="flex-grow overflow-auto p-4 space-y-4">
        {reports.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            Ask the AI to calculate a traverse closure to see reports here.
          </div>
        ) : (
          reports.map(report => (
            <div key={report.id} className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 light-theme:bg-gray-100/50 light-theme:border-gray-300">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="font-semibold text-lg text-green-400">{report.name}</h4>
                  <p className="text-xs text-gray-400">Generated: {new Date(report.createdAt).toLocaleString()}</p>
                </div>
                <button onClick={() => handleExportTxt(report)} className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors duration-200 bg-green-600 text-white hover:bg-green-700">
                    <DownloadIcon className="w-4 h-4" />
                    Export .txt
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-900/50 p-3 rounded-md light-theme:bg-gray-200/50">
                    <h5 className="font-semibold text-gray-300 mb-2 light-theme:text-gray-600">Statistics</h5>
                    <p><strong>Total Distance:</strong> {report.totalDistance.toFixed(2)}'</p>
                    <p><strong>Area:</strong> {report.area.toFixed(2)} sq. ft. ({(report.area / 43560).toFixed(4)} acres)</p>
                </div>
                 <div className="bg-gray-900/50 p-3 rounded-md light-theme:bg-gray-200/50">
                    <h5 className="font-semibold text-gray-300 mb-2 light-theme:text-gray-600">Misclosure</h5>
                    <p><strong>ΔN:</strong> {report.misclosureNorthing.toFixed(4)}'</p>
                    <p><strong>ΔE:</strong> {report.misclosureEasting.toFixed(4)}'</p>
                    <p><strong>Distance:</strong> {report.misclosureDistance.toFixed(4)}'</p>
                    <p><strong>Bearing:</strong> {report.misclosureBearing}</p>
                    <p className="font-bold text-green-300"><strong>Precision:</strong> {report.precision}</p>
                </div>
              </div>
              <div className="mt-4">
                  <h5 className="font-semibold text-gray-300 mb-2 light-theme:text-gray-600">Traverse</h5>
                  <div className="max-h-48 overflow-y-auto bg-gray-900/50 p-2 rounded-md font-mono text-xs light-theme:bg-gray-200/50">
                    {report.lines.map((line, idx) => (
                        <p key={idx}>{`${line.from} -> ${line.to}`.padEnd(20)} {line.bearing.padEnd(20)} {line.distance}</p>
                    ))}
                  </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ClosureReportPanel;
